import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "影视", to: "/category/movie" },
  { label: "动漫", to: "/category/anime" },
  { label: "音乐", to: "/category/music" },
  { label: "关于", to: "/about" },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [mobileKeyword, setMobileKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const goSearch = (value: string) => {
    const q = value.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  return (
    <header className="sticky top-0 z-40 bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="hidden md:block bg-primary-950 text-background-200">
        <div className="wrap flex items-center justify-between h-9 text-xs">
          <p className="flex items-center gap-2">
            <i className="ri-flashlight-fill text-accent-400" aria-hidden="true"></i>
            高清资源资讯平台 · 每日整理最新内容与版本动态
          </p>
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-accent-400" aria-hidden="true"></i>
              今日更新 24 条
            </span>
            <Link to="/about" className="cursor-pointer hover:text-accent-300 transition-colors">
              关于我们
            </Link>
            <a href="mailto:hello@wuma-portal.example" className="cursor-pointer hover:text-accent-300 transition-colors">
              hello@wuma-portal.example
            </a>
          </div>
        </div>
      </div>

      <div className="wrap flex items-center h-16 gap-4 lg:gap-8">
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer shrink-0">
          <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center font-heading font-extrabold text-lg">
            无
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading font-extrabold text-lg text-foreground-950 tracking-tight">无码</span>
            <span className="text-[10px] text-foreground-500 mt-1 tracking-wide">HIGH-DEF MEDIA</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${
                  isActive
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-600 hover:text-foreground-950 hover:bg-background-100"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form
          className="hidden md:flex items-center ml-auto relative w-[240px] lg:w-[300px]"
          onSubmit={(event) => {
            event.preventDefault();
            goSearch(keyword);
          }}
          role="search"
        >
          <i
            className="ri-search-line absolute left-3 text-foreground-400 text-sm pointer-events-none"
            aria-hidden="true"
          ></i>
          <input
            type="search"
            name="q"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索影视、动漫、音乐资源"
            aria-label="全站资源搜索"
            className="w-full h-9 pl-9 pr-16 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
          <button
            type="submit"
            className="absolute right-1 h-7 px-3 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          aria-label={menuOpen ? "收起菜单" : "展开菜单"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((prev) => !prev)}
          className="md:hidden ml-auto w-10 h-10 rounded-md border border-background-300 bg-background-50 flex items-center justify-center text-foreground-800 cursor-pointer"
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} aria-hidden="true"></i>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 animate-soft-in">
          <div className="wrap py-4">
            <form
              className="flex items-center gap-2 mb-3"
              onSubmit={(event) => {
                event.preventDefault();
                setMenuOpen(false);
                goSearch(mobileKeyword);
              }}
              role="search"
            >
              <div className="relative flex-1 min-w-0">
                <i
                  className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"
                  aria-hidden="true"
                ></i>
                <input
                  type="search"
                  name="q"
                  value={mobileKeyword}
                  onChange={(event) => setMobileKeyword(event.target.value)}
                  placeholder="搜索资源关键词"
                  aria-label="搜索资源关键词"
                  className="w-full h-10 pl-9 pr-3 rounded-md border border-background-300 bg-background-50 text-sm focus:outline-none focus:border-primary-400"
                />
              </div>
              <button
                type="submit"
                className="h-10 px-4 rounded-md bg-accent-500 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>
            <nav className="grid grid-cols-2 gap-2" aria-label="移动端导航">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-md text-sm font-medium border cursor-pointer ${
                      isActive
                        ? "bg-primary-50 border-primary-200 text-primary-700"
                        : "bg-background-50 border-background-200 text-foreground-700"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}

export function MobileSearchBar() {
  const [value, setValue] = useState("");
  const navigate = useNavigate();

  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/97 backdrop-blur border-t border-background-200">
      <form
        className="px-3 py-2.5 flex items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const q = value.trim();
          navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
        }}
        role="search"
      >
        <div className="relative flex-1 min-w-0">
          <i
            className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"
            aria-hidden="true"
          ></i>
          <input
            type="search"
            name="q"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder="随时搜索高清资源"
            aria-label="随时搜索高清资源"
            className="w-full h-10 pl-9 pr-3 rounded-md border border-background-300 bg-background-50 text-sm focus:outline-none focus:border-primary-400"
          />
        </div>
        <button
          type="submit"
          className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
        >
          搜索
        </button>
      </form>
    </div>
  );
}